import React, { useState, useEffect } from 'react';
import { tasksAPI, casesAPI, employeesAPI } from '../services/api';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import StatusBadge from '../components/StatusBadge';
import { Plus, Search, Edit2, Trash2, CheckCircle2 } from 'lucide-react';
import { toast } from 'react-toastify';

const EMPTY = { case: '', assigned_to: '', title: '', description: '', due_date: '', priority: 'Medium', status: 'Not Started', billable: true, time_estimate_hours: 0 };

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [cases, setCases] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      const { data } = await tasksAPI.list(params);
      setTasks(data.results || data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    casesAPI.list().then(r => setCases(r.data.results || r.data));
    employeesAPI.list().then(r => setEmployees(r.data.results || r.data));
  }, []);

  useEffect(() => { load(); }, [search, statusFilter, priorityFilter]);

  const quickComplete = async (t) => {
    await tasksAPI.update(t.id, { status: 'Completed' });
    toast.success('Task marked complete');
    load();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, billable: form.billable === true || form.billable === 'true' };
      if (editing) { await tasksAPI.update(editing.id, payload); toast.success('Task updated'); }
      else { await tasksAPI.create(payload); toast.success('Task created'); }
      setModalOpen(false);
      load();
    } catch { toast.error('Failed to save task'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    await tasksAPI.delete(id);
    toast.success('Task deleted');
    load();
  };

  const f = (name) => ({ value: form[name] ?? '', onChange: e => setForm({ ...form, [name]: e.target.value }) });

  const priorityColor = (p) => ({ High: 'border-l-red-500', Medium: 'border-l-amber-500', Low: 'border-l-blue-400' })[p] || '';

  return (
    <div className="p-6">
      <PageHeader
        title="Tasks"
        subtitle={`${tasks.length} tasks`}
        actions={<button className="btn-primary" onClick={() => { setEditing(null); setForm(EMPTY); setModalOpen(true); }}><Plus size={16} />New Task</button>}
      />

      <div className="card mb-5 p-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className="input pl-9" placeholder="Search tasks..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="input sm:w-40" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          {['Not Started', 'In Progress', 'Completed', 'Cancelled'].map(s => <option key={s}>{s}</option>)}
        </select>
        <select className="input sm:w-36" value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)}>
          <option value="">All Priority</option>
          {['High', 'Medium', 'Low'].map(s => <option key={s}>{s}</option>)}
        </select>
      </div>

      {/* Kanban-style cards */}
      <div className="space-y-2">
        {loading ? <div className="text-center py-12 text-gray-400">Loading...</div>
        : tasks.length === 0 ? <div className="card text-center py-12 text-gray-400">No tasks found.</div>
        : tasks.map(t => (
          <div key={t.id} className={`card p-4 border-l-4 ${priorityColor(t.priority)} hover:shadow-md transition-shadow`}>
            <div className="flex items-start gap-3">
              <button onClick={() => quickComplete(t)} className={`mt-0.5 flex-shrink-0 ${t.status === 'Completed' ? 'text-green-500' : 'text-gray-300 hover:text-green-500'}`}>
                <CheckCircle2 size={18} />
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <p className={`font-medium text-gray-900 ${t.status === 'Completed' ? 'line-through text-gray-400' : ''}`}>{t.title}</p>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <StatusBadge status={t.priority} />
                    <StatusBadge status={t.status} />
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3 mt-1">
                  <span className="text-xs text-gray-500">{t.case_title}</span>
                  {t.assigned_to_name && <span className="text-xs text-blue-600">{t.assigned_to_name}</span>}
                  {t.due_date && <span className="text-xs text-gray-400">Due: {t.due_date}</span>}
                  {t.time_estimate_hours > 0 && <span className="text-xs text-gray-400">Est: {t.time_estimate_hours}h</span>}
                  {t.billable && <span className="text-xs bg-green-50 text-green-700 px-1.5 py-0.5 rounded">Billable</span>}
                </div>
                {t.description && <p className="text-xs text-gray-400 mt-1 truncate">{t.description}</p>}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button className="p-1.5 text-gray-400 hover:text-amber-600" onClick={() => { setEditing(t); setForm({ ...t }); setModalOpen(true); }}><Edit2 size={14} /></button>
                <button className="p-1.5 text-gray-400 hover:text-red-600" onClick={() => handleDelete(t.id)}><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Task' : 'New Task'}>
        <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="label">Title *</label>
            <input className="input" required {...f('title')} />
          </div>
          <div>
            <label className="label">Case *</label>
            <select className="input" required {...f('case')}>
              <option value="">Select case</option>
              {cases.map(c => <option key={c.id} value={c.id}>{c.case_title}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Assigned To</label>
            <select className="input" {...f('assigned_to')}>
              <option value="">Unassigned</option>
              {employees.map(e => <option key={e.id} value={e.id}>{e.full_name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Priority</label>
            <select className="input" {...f('priority')}>
              {['High', 'Medium', 'Low'].map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Status</label>
            <select className="input" {...f('status')}>
              {['Not Started', 'In Progress', 'Completed', 'Cancelled'].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Due Date</label>
            <input type="date" className="input" {...f('due_date')} />
          </div>
          <div>
            <label className="label">Time Estimate (hours)</label>
            <input type="number" step="0.5" min="0" className="input" {...f('time_estimate_hours')} />
          </div>
          <div className="col-span-2">
            <label className="label">Description</label>
            <textarea className="input" rows={3} value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="col-span-2 flex items-center gap-2">
            <input type="checkbox" id="billable" checked={form.billable === true || form.billable === 'true'} onChange={e => setForm({ ...form, billable: e.target.checked })} className="rounded" />
            <label htmlFor="billable" className="text-sm text-gray-700">Billable task</label>
          </div>
          <div className="col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving...' : editing ? 'Update' : 'Create Task'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
